import { useEffect, useState } from "react";
import { X, Skull, Crown, Lock } from "lucide-react";
import { api, absUrl } from "@/lib/api";

/**
 * The Codex — bestiary of every foe the player has slain.
 * Unlocked entries show portrait + lore + kill count + first-slain date.
 * Locked entries show a silhouette + `???` so the collector's loop is visible.
 * Total-known pulled from the story tree so completion % is meaningful.
 */
export default function CodexModal({ open, onClose }) {
  const [codex, setCodex] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/codex");
        if (alive) setCodex(r);
      } catch {
        if (alive) setCodex({ slain: [], count: 0, total_known: 0, completion: 0 });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  if (!open) return null;

  const slain = codex?.slain || [];
  const total = codex?.total_known || 0;
  const lockedCount = Math.max(0, total - slain.length);
  const lockedSlots = Array.from({ length: lockedCount });

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: "rgba(4,2,1,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="codex-modal-backdrop"
    >
      <div
        className="panel w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          background: "radial-gradient(ellipse at top, rgba(60,20,10,0.35), rgba(8,4,2,0.98))",
          borderColor: "rgba(212,175,55,0.55)",
          boxShadow: "0 0 40px rgba(212,175,55,0.15), inset 0 0 60px rgba(0,0,0,0.6)",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="codex-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[0.4em] gold-text">— THE ARCHIVIST&apos;S CODEX —</div>
            <div className="font-heading text-3xl italic text-[#f0e6d2] mt-1">Foes Slain</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="font-heading text-3xl gold-text" data-testid="codex-completion">
                {codex?.completion ?? 0}%
              </div>
              <div className="text-[10px] tracking-widest ash-text">
                {slain.length} / {total} KNOWN
              </div>
            </div>
            <button
              onClick={onClose}
              className="panel p-2 hover:border-[#d4af37] transition-colors"
              data-testid="codex-close-btn"
            >
              <X size={16} className="ash-text" />
            </button>
          </div>
        </div>

        {/* Content grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="text-center ash-text italic py-16">Turning the archivist&apos;s pages...</div>
          )}
          {!loading && slain.length === 0 && lockedCount === 0 && (
            <div className="text-center ash-text italic py-16">
              The archive is empty. Descend and claim your first name.
            </div>
          )}
          {!loading && (slain.length > 0 || lockedCount > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {slain.map((e) => (
                <CodexEntry key={e.slug} entry={e} />
              ))}
              {lockedSlots.map((_, i) => (
                <div
                  key={`locked-${i}`}
                  className="panel p-3 flex items-center gap-3 opacity-50"
                  style={{ borderColor: "rgba(255,255,255,0.08)" }}
                  data-testid={`codex-locked-${i}`}
                >
                  <div
                    className="w-14 h-14 flex items-center justify-center flex-shrink-0 border border-white/10 bg-black/60"
                  >
                    <Lock size={16} className="ash-text" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.3em] ash-text">UNKNOWN</div>
                    <div className="font-heading text-lg text-white/25 italic truncate">???</div>
                    <div className="text-[10px] font-narrative italic text-white/25 mt-0.5">
                      Slay this foe to record their name.
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/10 text-center text-[10px] tracking-[0.3em] ash-text">
          Every kill echoes forever in the Ember's memory.
        </div>
      </div>
    </div>
  );
}

function CodexEntry({ entry }) {
  const isBoss = !!entry.is_boss;
  return (
    <div
      className="panel p-3 flex items-center gap-3 hover:border-[#d4af37]/60 transition-colors"
      style={isBoss ? { borderColor: "rgba(226,88,34,0.55)", boxShadow: "0 0 14px rgba(226,88,34,0.15)" } : {}}
      data-testid={`codex-entry-${entry.slug}`}
    >
      <div
        className="w-14 h-14 relative flex-shrink-0 overflow-hidden border border-white/15 bg-black/60"
        style={isBoss ? { boxShadow: "0 0 8px rgba(226,88,34,0.5) inset" } : {}}
      >
        {entry.portrait_url ? (
          <img
            src={absUrl(entry.portrait_url)}
            alt={entry.enemy_name}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : isBoss ? (
          <Skull size={22} className="ember absolute inset-0 m-auto" />
        ) : (
          <Skull size={20} className="ash-text absolute inset-0 m-auto" />
        )}
        {isBoss && (
          <div className="absolute top-0 right-0 bg-[#c0392b] text-[#f0e6d2] text-[7px] px-1 py-0.5 tracking-widest">
            <Crown size={7} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className={`font-heading text-lg italic truncate ${isBoss ? "ember" : "text-[#f0e6d2]"}`}>
            {entry.enemy_name}
          </div>
          <div className="text-[10px] tracking-widest ash-text flex-shrink-0" data-testid={`codex-kills-${entry.slug}`}>
            ×{entry.kills}
          </div>
        </div>
        <div className="text-[10px] font-narrative italic ash-text mt-0.5 line-clamp-2">
          {entry.lore || "The archivist has no record of this one."}
        </div>
      </div>
    </div>
  );
}
